/**
 * TrpcMyDataReader — the production `MyDataPort` adapter over the typed tRPC
 * client (033 US1, T024). Calls the live `member.myData.view` procedure: the
 * member's cookie session authenticates the request (same-origin fetch, the
 * same seam `session.me` rides), and `memberProcedure` on the API side
 * guarantees only a member session can read it.
 *
 * The `member` namespace is OPTIONAL on the published `AppRouter` (the
 * conditional-spread posture — production mounts it since the 033 US1
 * activation; some test compositions omit it), so the adapter narrows before
 * calling and fails loudly if wired against a router without the namespace.
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  MyDataAnalyticsPage,
  MyDataPort,
  MyDataSuccessfulMemberMessagesPage,
  MyDataView,
} from './MyDataPort.js';

export class TrpcMyDataReader implements MyDataPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async getMyData(): Promise<MyDataView> {
    const member = this.#client.member;
    // Both `member` and `member.myData` are optional on the published router
    // (each member sub-surface mounts independently). Absence is a composition
    // error, not a runtime condition — loud > silent empty view (P7).
    if (member?.myData === undefined) {
      throw new Error(
        'TrpcMyDataReader: member.myData is absent on the AppRouter — ' +
          'the My-data surface requires the 033 US1 API activation.',
      );
    }
    return member.myData.view.query();
  }

  async getAnalyticsPage(cursor?: string): Promise<MyDataAnalyticsPage> {
    const member = this.#client.member;
    if (member?.myData === undefined) {
      throw new Error(
        'TrpcMyDataReader: member.myData is absent on the AppRouter — ' +
          'the My-data Analytics surface requires the 047 API activation.',
      );
    }
    return await member.myData.analytics.query(cursor === undefined ? {} : { cursor });
  }

  async getSuccessfulMemberMessagesPage(
    cursor?: string,
  ): Promise<MyDataSuccessfulMemberMessagesPage> {
    const member = this.#client.member;
    if (member?.myData === undefined) {
      throw new Error(
        'TrpcMyDataReader: member.myData is absent on the AppRouter — ' +
          'the My-data successful-message surface requires the 047 API activation.',
      );
    }
    return await member.myData.successfulMemberMessages.query(
      cursor === undefined ? {} : { cursor },
    );
  }
}
